'use client';

import React, { useState, useCallback, useId } from 'react';
import { cn } from '../lib/utils';

export interface Tab {
  label: string;
  value: string;
  content: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
}

export interface TabsProps {
  tabs: Tab[];
  activeTab?: string;
  onChange?: (value: string) => void;
  className?: string;
  variant?: 'default' | 'pills' | 'underline';
}

export function Tabs({
  tabs,
  activeTab: controlledActive,
  onChange,
  className,
  variant = 'default',
}: TabsProps) {
  const id = useId();
  const [internalActive, setInternalActive] = useState(tabs[0]?.value ?? '');
  const activeValue = controlledActive ?? internalActive;

  const handleTabChange = useCallback(
    (value: string) => {
      if (controlledActive === undefined) {
        setInternalActive(value);
      }
      onChange?.(value);
    },
    [controlledActive, onChange],
  );

  const activeTabObj = tabs.find((t) => t.value === activeValue);

  const tabListClasses: Record<string, string> = {
    default: 'flex gap-1 rounded-lg bg-mist p-1',
    pills: 'flex gap-2',
    underline: 'flex gap-0 border-b border-slate-200',
  };

  const getTabClasses = (isActive: boolean, isDisabled: boolean) => {
    const base =
      'inline-flex shrink-0 items-center gap-2 whitespace-nowrap font-body text-sm font-medium transition-interactive duration-200';
    if (isDisabled) return cn(base, 'cursor-not-allowed opacity-50');

    switch (variant) {
      case 'pills':
        return cn(
          base,
          'rounded-lg px-4 py-2',
          isActive
            ? 'bg-statura-ink text-white shadow-sm'
            : 'text-slate-600 hover:bg-slate-100 hover:text-text-heading',
        );
      case 'underline':
        return cn(
          base,
          'px-4 py-3 border-b-2 -mb-px',
          isActive
            ? 'border-statura-green-accessible text-text-heading'
            : 'border-transparent text-slate-500 hover:text-text-heading hover:border-slate-300',
        );
      default:
        return cn(
          base,
          'rounded-lg px-4 py-2',
          isActive
            ? 'bg-white text-text-heading shadow-sm'
            : 'text-slate-600 hover:text-text-heading',
        );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number) => {
    const currentTab = tabs[currentIndex];
    if (!currentTab) return;
    const enabledTabs = tabs.filter((t) => !t.disabled);
    const currentEnabledIndex = enabledTabs.findIndex((t) => t.value === currentTab.value);

    let nextTab: Tab | undefined;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      nextTab = enabledTabs[(currentEnabledIndex + 1) % enabledTabs.length];
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      nextTab = enabledTabs[(currentEnabledIndex - 1 + enabledTabs.length) % enabledTabs.length];
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextTab = enabledTabs[0];
    } else if (e.key === 'End') {
      e.preventDefault();
      nextTab = enabledTabs[enabledTabs.length - 1];
    }

    if (nextTab) {
      handleTabChange(nextTab.value);
      const tabEl = document.getElementById(`${id}-tab-${nextTab.value}`);
      tabEl?.focus();
    }
  };

  return (
    <div className={cn('w-full min-w-0', className)}>
      <div className="max-w-full overflow-x-auto pb-1">
        <div
          role="tablist"
          aria-orientation="horizontal"
          className={cn('w-max min-w-full', tabListClasses[variant])}
        >
          {tabs.map((tab, index) => {
            const isActive = tab.value === activeValue;
            return (
              <button
                key={tab.value}
                id={`${id}-tab-${tab.value}`}
                role="tab"
                type="button"
                aria-selected={isActive}
                aria-controls={`${id}-panel-${tab.value}`}
                tabIndex={isActive ? 0 : -1}
                disabled={tab.disabled}
                onClick={() => !tab.disabled && handleTabChange(tab.value)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={getTabClasses(isActive, !!tab.disabled)}
              >
                {tab.icon}
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {activeTabObj && (
        <div
          id={`${id}-panel-${activeTabObj.value}`}
          role="tabpanel"
          aria-labelledby={`${id}-tab-${activeTabObj.value}`}
          tabIndex={0}
          className="mt-4 focus-visible:outline-none"
        >
          {activeTabObj.content}
        </div>
      )}
    </div>
  );
}
